import {match} from 'ts-pattern';

export type RouteMarkerKind = 'start' | 'head' | 'finish';

export type RouteMarkerColors = {
  start: string;
  head: string;
  ink: string;
  outline: string;
};

// Start and finish share one size so neither end of the route looks more important.
const END_MARKER_SIZE = 28;
const END_MARKER_CENTER = END_MARKER_SIZE / 2;

/** Pixel size of each marker image and the point of it that sits on the route. */
export const routeMarkerGeometry: Record<
  RouteMarkerKind,
  {width: number; height: number; anchorX: number; anchorY: number}
> = {
  start: {
    width: END_MARKER_SIZE,
    height: END_MARKER_SIZE,
    anchorX: END_MARKER_CENTER,
    anchorY: END_MARKER_CENTER,
  },
  head: {width: 24, height: 24, anchorX: 12, anchorY: 12},
  finish: {
    width: END_MARKER_SIZE,
    height: END_MARKER_SIZE,
    anchorX: END_MARKER_CENTER,
    anchorY: END_MARKER_CENTER,
  },
};

const FINISH_CHECKER_RADIUS = 10.5;
const FINISH_CHECKER_SQUARES = 5;

// A round checkerboard: squares on a white disc, clipped to a circle around the given centre.
const getRoundCheckerboard = (center: number, ink: string, paper: string) => {
  const size = (FINISH_CHECKER_RADIUS * 2) / FINISH_CHECKER_SQUARES;
  const origin = center - FINISH_CHECKER_RADIUS;
  const indexes = Array.from({length: FINISH_CHECKER_SQUARES}, (_, index) => index);
  const squares = indexes.flatMap((row) =>
    indexes
      .filter((column) => (row + column) % 2 === 0)
      .map(
        (column) =>
          `<rect x="${origin + column * size}" y="${origin + row * size}" width="${size}" height="${size}" fill="${ink}"/>`
      )
  );
  return `<clipPath id="finish-clip"><circle cx="${center}" cy="${center}" r="${FINISH_CHECKER_RADIUS}"/></clipPath><g clip-path="url(#finish-clip)"><rect x="${origin}" y="${origin}" width="${FINISH_CHECKER_RADIUS * 2}" height="${FINISH_CHECKER_RADIUS * 2}" fill="${paper}"/>${squares.join('')}</g>`;
};

const getMarkerSvg = (kind: RouteMarkerKind, colors: RouteMarkerColors) =>
  match(kind)
    .with(
      'start',
      () =>
        `<circle cx="14" cy="14" r="12" fill="${colors.start}" stroke="${colors.outline}" stroke-width="2.5"/><path d="M11.5 9.5l7 4.5-7 4.5z" fill="${colors.outline}"/>`
    )
    .with(
      'head',
      () =>
        `<circle cx="12" cy="12" r="11" fill="${colors.head}" fill-opacity="0.2"/><circle cx="12" cy="12" r="6" fill="${colors.head}" stroke="${colors.outline}" stroke-width="2.5"/>`
    )
    .with(
      'finish',
      () =>
        `<circle cx="14" cy="14" r="13.5" fill="${colors.outline}"/><circle cx="14" cy="14" r="13.5" fill="none" stroke="${colors.ink}" stroke-opacity="0.15"/>${getRoundCheckerboard(END_MARKER_CENTER, colors.ink, colors.outline)}`
    )
    .exhaustive();

/**
 * The route markers as SVG images. Legacy markers take an image URL, which keeps the markers
 * crisp and needs no map ID (a map ID would turn off the muted map style).
 */
export const getRouteMarkerIconUrl = (kind: RouteMarkerKind, colors: RouteMarkerColors) => {
  const {width, height} = routeMarkerGeometry[kind];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" id="route-marker-${kind}" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${getMarkerSvg(kind, colors)}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
